import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const PHASE_COLORS = {
  A: "text-[#E11D48]",
  B: "text-[#16A34A]",
  C: "text-[#2563EB]",
};

function fmt(value, digits = 4) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toFixed(digits);
}

/**
 * Renders the per-phase "snapshot" DOE schema produced by the multiconductor
 * (3-phase) engine: { success, algorithm, summary, busResults: [{ busIdx,
 * name, phases: [{ phase, vmBasePu, maxExportMw, maxImportMw }], totalExportMw,
 * totalImportMw }] }.
 */
export default function MulticonductorDOEResults({ result }) {
  if (!result) return null;

  if (result.success === false) {
    return (
      <div
        className="border border-dashed border-border rounded-[2px] p-8 text-center bg-secondary/20 text-xs font-mono text-muted-foreground"
        data-testid="mc-doe-error"
      >
        {result.error || "Multiconductor DOE calculation returned no result."}
      </div>
    );
  }

  const summary = result.summary || {};
  const buses = Array.isArray(result.busResults) ? result.busResults : [];

  return (
    <div data-testid="mc-doe-results">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-[2px] overflow-hidden mb-8">
        <SummaryTile
          label="Σ Export Envelope"
          value={`${fmt(summary.totalExportMw, 3)} MW`}
          accent="text-[var(--doe-export)]"
          testid="mc-kpi-export"
        />
        <SummaryTile
          label="Σ Import Envelope"
          value={`${fmt(summary.totalImportMw, 3)} MW`}
          accent="text-[var(--doe-import)]"
          testid="mc-kpi-import"
        />
        <SummaryTile
          label="DER Buses"
          value={`${summary.derBusCount ?? "—"} / ${summary.busCount ?? "—"}`}
          testid="mc-kpi-der"
        />
        <SummaryTile
          label="Voltage Band (p.u.)"
          value={`${fmt(summary.minVoltPu, 3)} – ${fmt(summary.maxVoltPu, 3)}`}
          testid="mc-kpi-voltage"
        />
      </div>

      {/* Per-bus per-phase table */}
      <div
        className="border border-border rounded-[2px] overflow-x-auto bg-background"
        data-testid="mc-doe-table"
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60 hover:bg-secondary/60">
              <TableHead className="text-[10px] uppercase tracking-[0.18em] font-semibold w-24">
                Bus
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.18em] font-semibold">
                Name
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.18em] font-semibold w-16">
                Phase
              </TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] font-semibold">
                V<sub>base</sub> (p.u.)
              </TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--doe-export)]">
                Max export (MW)
              </TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--doe-import)]">
                Max import (MW)
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {buses.map((bus) => {
              const phases =
                Array.isArray(bus.phases) && bus.phases.length
                  ? bus.phases
                  : [{ phase: "—", vmBasePu: null, maxExportMw: null, maxImportMw: null }];
              return phases.map((ph, idx) => (
                <TableRow
                  key={`${bus.busIdx}-${ph.phase}`}
                  className="font-mono text-xs"
                  data-testid={`mc-doe-row-${bus.busIdx}-${ph.phase}`}
                >
                  {idx === 0 ? (
                    <>
                      <TableCell
                        className="font-semibold align-top"
                        rowSpan={phases.length}
                      >
                        #{bus.busIdx}
                      </TableCell>
                      <TableCell
                        className="align-top text-muted-foreground"
                        rowSpan={phases.length}
                      >
                        {bus.name}
                      </TableCell>
                    </>
                  ) : null}
                  <TableCell
                    className={`font-semibold ${PHASE_COLORS[ph.phase] || ""}`}
                  >
                    {ph.phase}
                  </TableCell>
                  <TableCell className="text-right">{fmt(ph.vmBasePu, 4)}</TableCell>
                  <TableCell className="text-right text-[var(--doe-export)] font-semibold">
                    {fmt(ph.maxExportMw, 4)}
                  </TableCell>
                  <TableCell className="text-right text-[var(--doe-import)] font-semibold">
                    {fmt(ph.maxImportMw, 4)}
                  </TableCell>
                </TableRow>
              ));
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, accent = "text-foreground", testid }) {
  return (
    <div className="bg-background px-5 py-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
        {label}
      </div>
      <div className={`mt-2 text-2xl font-mono font-semibold tracking-tight ${accent}`}>
        {value}
      </div>
    </div>
  );
}
